import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Link as MuiLink, Snackbar, Typography } from '@mui/material';
import { AutoAwesome, DescriptionOutlined, EventRepeat, FolderOpen, SlideshowOutlined, VideocamOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useApp } from '../../context/AppContext';
import { useAIRequests } from '../../context/AIRequestContext';
import { loadReadyForYou, AUTOPILOT_REQUEST_TYPE } from '../../services/autopilotService';
import { buildReadyItem, goGeneratePath, unfiledReason } from '../../services/autopilotModel';
import { isPresentationTemplateType } from '../documents/documentMeta';
import { sessionPath } from '../../utils/navigation';
import { formatDate } from '../../utils/dateUtils';
import { IconTile, ListSkeleton } from '../pagekit';
import FilingControls from './FilingControls';
import { FOLLOW_UP_ANCHOR } from './FollowUpCard';

/** Rows shown on Home. */
export const READY_FOR_YOU_LIMIT = 6;

/**
 * Signature of the autopilot jobs the app knows about, so the list reloads when one finishes.
 * @param {Array<object>} rows - ai_requests rows.
 * @returns {string} Signature.
 */
function autopilotJobsSignature(rows) {
  return (rows || [])
    .filter((r) => r.type === AUTOPILOT_REQUEST_TYPE)
    .map((r) => `${r.id}:${r.status}`)
    .sort()
    .join(',');
}

/**
 * One "Ready for you" row.
 * @param {object} props - Props.
 * @param {object} props.item - Row from buildReadyItem.
 * @param {(event: {message: string}) => void} props.onChanged - After Undo / File it.
 * @returns {JSX.Element} Row.
 */
function ReadyRow({ item, onChanged }) {
  const summaryTab = sessionPath(item.sessionId, 'summary');
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        display: 'flex',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderTop: 1,
        borderColor: 'var(--q-row-divider)',
        '&:first-of-type': { borderTop: 0 },
      }}
    >
      <IconTile tone="neutral" size={32}><VideocamOutlined /></IconTile>
      <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <MuiLink
            component={RouterLink}
            to={summaryTab}
            underline="hover"
            sx={{ fontWeight: 600, fontSize: 14, color: 'text.primary', overflowWrap: 'anywhere' }}
          >
            {item.title}
          </MuiLink>
          {item.createdAt && (
            <Typography component="span" sx={{ fontSize: 12.5, color: 'var(--q-faint)' }}>{formatDate(item.createdAt)}</Typography>
          )}
          {item.running && (
            <Chip size="small" icon={<CircularProgress size={12} aria-hidden="true" />} label="Autopilot is working on it" />
          )}
          {item.failed && <Chip size="small" color="warning" variant="outlined" label="Autopilot could not finish" />}
        </Box>

        {item.summaryLine && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {item.summaryLine}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {item.filingPath ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<FolderOpen fontSize="inherit" />}
              label={item.filingBadge ? `${item.filingPath} (${item.filingBadge})` : item.filingPath}
              component={RouterLink}
              to={sessionPath(item.sessionId)}
              clickable
            />
          ) : !item.running && (
            <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {item.filingState === 'undone' ? 'Unfiled' : unfiledReason(item.filing)}
            </Typography>
          )}
          {!item.running && (item.canUndo || !item.projectId) && (
            <FilingControls
              sessionId={item.sessionId}
              sessionName={item.title}
              filing={item.canUndo ? item.filing : null}
              canUndo={item.canUndo}
              currentProjectId={item.projectId}
              changeLabel={item.projectId ? 'Change' : 'File it'}
              onChanged={onChanged}
            />
          )}
          {item.followUp && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              icon={<EventRepeat fontSize="inherit" />}
              label="Follow-up suggested"
              component={RouterLink}
              to={`${summaryTab}#${FOLLOW_UP_ANCHOR}`}
              clickable
              title={item.followUp.topic}
            />
          )}
        </Box>

        {/* One-click documents need the meeting's project, so they show once it is filed. */}
        {item.projectId && item.templates.length > 0 && (
          <Box role="group" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }} aria-label="Suggested documents">
            {item.templates.map((t) => {
              const reasonId = `ready-${item.sessionId}-${t.templateId}-reason`;
              return (
                <Box key={t.templateId} sx={{ display: 'grid', gap: 0.25, minWidth: 0, maxWidth: 320 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={goGeneratePath(item.sessionId, t.templateId, 'app')}
                    startIcon={isPresentationTemplateType(t.templateType) ? <SlideshowOutlined /> : <DescriptionOutlined />}
                    aria-describedby={t.reason ? reasonId : undefined}
                    sx={{ textTransform: 'none', justifySelf: 'start' }}
                  >
                    Create {t.name}
                  </Button>
                  {t.reason && (
                    <Typography id={reasonId} component="span" sx={{ fontSize: 12, color: 'text.secondary', overflowWrap: 'anywhere' }}>
                      {t.reason}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Home's "Ready for you": the latest Meeting Autopilot results (meeting, one-line summary, where
 * it was filed with Change / Undo, a follow-up chip and one-click template buttons).
 * @returns {JSX.Element} Section.
 */
export default function ReadyForYou() {
  const { state: authState } = useAuth();
  const { state: appState } = useApp();
  const { activeRequests, completedRequests } = useAIRequests();
  const accountId = authState.activeAccount?.id || null;
  const userId = authState.user?.id || null;
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [version, setVersion] = useState(0);

  const jobsSignature = useMemo(
    () => autopilotJobsSignature([...(activeRequests || []), ...(completedRequests || [])]),
    [activeRequests, completedRequests]
  );

  useEffect(() => {
    const onChanged = (event) => {
      if (['session', 'customer', 'project'].includes(event?.detail?.kind)) setVersion((v) => v + 1);
    };
    window.addEventListener('quillai:data-changed', onChanged);
    return () => window.removeEventListener('quillai:data-changed', onChanged);
  }, []);

  useEffect(() => {
    if (!accountId || !userId) {
      setEntries([]);
      return undefined;
    }
    let cancelled = false;
    setError('');
    loadReadyForYou({ accountId, userId, limit: READY_FOR_YOU_LIMIT })
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setEntries([]);
          setError('We could not load your latest meetings.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, userId, version, jobsSignature]);

  const items = useMemo(
    () => (entries || []).map(({ session, insights, record }) => buildReadyItem({
      session,
      insights,
      record,
      projects: appState.projects || [],
      customers: appState.customers || [],
      templates: appState.templates || [],
    })),
    [entries, appState.projects, appState.customers, appState.templates]
  );

  const handleChanged = useCallback(({ message }) => {
    setToast(message);
    setVersion((v) => v + 1);
  }, []);

  return (
    <Box
      component="section"
      aria-labelledby="home-ready-heading"
      sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', py: 1, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2.5, pt: 1.25, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesome sx={{ fontSize: 18, color: 'var(--q-accent-text)' }} />
          <Typography id="home-ready-heading" component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>Ready for you</Typography>
        </Box>
        <MuiLink component={RouterLink} to="/customers/sessions" underline="hover" sx={{ fontSize: 13, fontWeight: 500 }}>All recordings</MuiLink>
      </Box>
      {error && (
        <Box sx={{ px: 2.5, pb: 1 }}>
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setVersion((v) => v + 1)}>Retry</Button>}>{error}</Alert>
        </Box>
      )}
      {entries === null ? (
        <ListSkeleton rows={3} label="Loading your latest meetings" />
      ) : items.length === 0 ? (
        !error && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
            Nothing new yet. After your next recorded meeting, its summary, where Quill filed it and the documents it suggests show up here.
          </Typography>
        )
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0 }} aria-label="Latest meetings">
          {items.map((item) => <ReadyRow key={item.sessionId} item={item} onChanged={handleChanged} />)}
        </Box>
      )}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
